import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Plus, Trash2, Loader2, Users, Edit } from "lucide-react";
import { format } from "date-fns";
import type { Account, AccountPeople, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { ContactFormModal } from "@/components/contact-form-modal";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildNavigationUrl } from "@/lib/breadcrumbs";

interface PartnerWithContact extends AccountPeople {
  contact?: Contact;
  // Extended fields returned by API but not in base schema
  partnerRole?: string;
  ownershipPercentage?: number;
}

interface PartnersPanelProps {
  account: Account;
}

export function PartnersPanel({ account }: PartnersPanelProps) {
  // All hooks must be called before any early returns (React rules of hooks)
  const [, navigate] = useLocation();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerWithContact | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [partnerRole, setPartnerRole] = useState<string>("managing_partner");
  const [equityPercentage, setEquityPercentage] = useState<number>(0);
  const [appointmentDate, setAppointmentDate] = useState<Date | undefined>();
  const [ceasedDate, setCeasedDate] = useState<Date | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  // Only show for partnership entity type
  const isPartnership = account.entityType === "partnership";

  const { data: partners = [], isLoading } = useQuery<PartnerWithContact[]>({
    queryKey: ["/api/crm/accounts", account.id, "people", { role: "partner" }],
    enabled: isPartnership,
  });

  const { data: _contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { clientGroupId: account.clientGroupId }],
    enabled: isAddDialogOpen && isPartnership,
  });

  const addMutation = useMutation({
    mutationFn: async (data: { contactId: string; role: string; equityPercentage?: number; appointmentDate?: string }) => {
      await apiRequest("POST", `/api/crm/accounts/${account.id}/people`, {
        contactId: data.contactId,
        role: "partner",
        partnerRole: data.role, // managing_partner or limited_partner
        ownershipPercentage: data.equityPercentage,
        appointmentDate: data.appointmentDate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "partners"] });
      toast({ title: "Partner added", description: "The partner has been added successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add partner", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/people/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "partners"] });
      toast({ title: "Partner removed", description: "The partner has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove partner", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      role?: string;
      equityPercentage?: number;
      appointmentDate?: string;
      ceasedDate?: string;
    }) => {
      const { id, ...updateData } = data;
      await apiRequest("PATCH", `/api/crm/accounts/${account.id}/people/${id}`, {
        ...updateData,
        partnerRole: updateData.role,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "partners"] });
      toast({ title: "Partner updated", description: "The partner has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update partner", variant: "destructive" });
    },
  });

  // Early return AFTER hooks to comply with React rules of hooks
  if (!isPartnership) {
    return null;
  }

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedContactId(null);
    setPartnerRole("managing_partner");
    setEquityPercentage(0);
    setAppointmentDate(undefined);
    setCeasedDate(undefined);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingPartner(null);
    setPartnerRole("managing_partner");
    setEquityPercentage(0);
    setAppointmentDate(undefined);
    setCeasedDate(undefined);
  };

  const handleOpenEdit = (partner: PartnerWithContact) => {
    setEditingPartner(partner);
    setPartnerRole(partner.partnerRole || "managing_partner");
    setEquityPercentage(partner.ownershipPercentage || 0);
    setAppointmentDate(partner.appointmentDate ? new Date(partner.appointmentDate) : undefined);
    setCeasedDate(partner.ceasedDate ? new Date(partner.ceasedDate) : undefined);
    setIsEditDialogOpen(true);
  };

  const handleAdd = () => {
    if (!selectedContactId) {
      toast({ title: "Validation Error", description: "Please select a contact", variant: "destructive" });
      return;
    }

    addMutation.mutate({
      contactId: selectedContactId,
      role: partnerRole,
      equityPercentage: equityPercentage || undefined,
      appointmentDate: appointmentDate ? format(appointmentDate, "yyyy-MM-dd") : undefined,
    });
  };

  const handleUpdate = () => {
    if (!editingPartner) return;
    updateMutation.mutate({
      id: editingPartner.id,
      role: partnerRole,
      equityPercentage: equityPercentage || undefined,
      appointmentDate: appointmentDate ? format(appointmentDate, "yyyy-MM-dd") : undefined,
      ceasedDate: ceasedDate ? format(ceasedDate, "yyyy-MM-dd") : undefined,
    });
  };

  const handleContactClick = (contactId: string) => {
    const url = buildNavigationUrl(`/contacts/${contactId}`, {
      from: "account",
      fromId: account.id,
      fromLabel: account.legalName,
    });
    navigate(url);
  };

  const handleContactCreated = (newContact: Contact) => {
    setSelectedContactId(newContact.id);
    setShowCreateContactDialog(false);
    toast({ title: "Contact created", description: "You can now select the newly created contact." });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Partners</h3>
          <Badge variant="secondary">{partners.length}</Badge>
        </div>
        <Button
          size="sm"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-partner"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Partner
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : partners.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-8 border rounded-md bg-white">
          No partners added yet
        </div>
      ) : (
        <div className="border rounded-md bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Partner Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Equity %</TableHead>
              <TableHead>Appointment Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.map((partner) => {
              const contactName = partner.contact
                ? `${partner.contact.firstName} ${partner.contact.lastName}`
                : "Unknown Contact";

              const isCurrent = !partner.ceasedDate || new Date(partner.ceasedDate) > new Date();

              return (
                <TableRow key={partner.id}>
                  <TableCell>
                    <button
                      onClick={() => partner.contactId && handleContactClick(partner.contactId)}
                      className="text-primary hover:underline text-left"
                    >
                      {contactName}
                    </button>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {partner.partnerRole === "managing_partner" ? "Managing Partner" : "Limited Partner"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {partner.ownershipPercentage ? `${partner.ownershipPercentage.toFixed(2)}%` : "-"}
                  </TableCell>
                  <TableCell>
                    {partner.appointmentDate ? format(new Date(partner.appointmentDate), "dd MMM yyyy") : "-"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={isCurrent ? "default" : "secondary"}>
                      {isCurrent ? "Current" : "Former"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(partner)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(partner.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      )}

      {/* Add Partner Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Partner</DialogTitle>
            <DialogDescription>
              Add a partner to {account.legalName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Contact</Label>
              <ContactSelect
                value={selectedContactId || ""}
                onValueChange={(value) => setSelectedContactId(value)}
                clientGroupId={account.clientGroupId || undefined}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateContactDialog(true)}
                className="mt-2 w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Contact
              </Button>
            </div>

            <div>
              <Label>Partner Role</Label>
              <Select value={partnerRole} onValueChange={setPartnerRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="managing_partner">Managing Partner</SelectItem>
                  <SelectItem value="limited_partner">Limited Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Equity Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={equityPercentage}
                onChange={(e) => setEquityPercentage(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label>Appointment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !appointmentDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {appointmentDate ? format(appointmentDate, "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={appointmentDate}
                    onSelect={setAppointmentDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={addMutation.isPending}>
              {addMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partner</DialogTitle>
            <DialogDescription>
              Update partner information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Partner Role</Label>
              <Select value={partnerRole} onValueChange={setPartnerRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="managing_partner">Managing Partner</SelectItem>
                  <SelectItem value="limited_partner">Limited Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Equity Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={equityPercentage}
                onChange={(e) => setEquityPercentage(parseFloat(e.target.value) || 0)}
              />
            </div>

            <div>
              <Label>Appointment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !appointmentDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {appointmentDate ? format(appointmentDate, "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={appointmentDate}
                    onSelect={setAppointmentDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label>Ceased Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !ceasedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {ceasedDate ? format(ceasedDate, "d MMM yyyy") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={ceasedDate}
                    onSelect={setCeasedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Creation Modal */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        onContactCreated={handleContactCreated}
        defaultClientGroupId={account.clientGroupId}
      />

      {/* Delete Confirmation (reusing director pattern) */}
      {deleteId && (
        <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove Partner</DialogTitle>
              <DialogDescription>
                Are you sure you want to remove this partner? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteId(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteId && deleteMutation.mutate(deleteId)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
